import { Link } from "react-router-dom";
import { ArrowUpRight, Users, ShieldCheck, Star } from "lucide-react";
import CarrierLogo from "./CarrierLogo";
import { openPartner } from "../lib/partnerAccess";

/**
 * Iter-189.5 (Sprint 4.5) — Marketplace strip for the insurance vertical.
 *
 * Renders ABOVE the direct-carrier table to fix the audit finding that
 * mixed marketplaces (The Zebra, LendingTree) with direct carriers in
 * the same ranked table was visually jarring (rows 1+2 had `—` for
 * premium, row 3 had real values) and damaged reader trust.
 *
 * Visual contract:
 *   - 1 or 2 marketplace cards in a 2-up grid (always full-width)
 *   - Each card carries the brand emerald accent
 *   - Premium fields are intentionally absent — replaced by the
 *     marketplace value prop ("100+ carriers", "Best multi-quote tool")
 *   - CTA uses the standardized verb: "Get Quote" (iter-190.J)
 *
 * Props mirror what `PartnerRateTable` already computes for its row
 * normalisation step (CarrierLogo, rating, affiliate_url) so this
 * component is a pure presentation layer — no new data fetching.
 *
 * Iter-275.P — CTA converted from `<a target="_blank">` to `<button>`
 * + `onClick={openPartner({...})}`. Anchors with target=_blank were
 * historically safe (browser-native single tab) but the user's
 * acceptance criterion now mandates a single `openPartner(...)` call
 * site for every partner CTA. The button preserves the visual styling
 * but routes through the window-level URL+placement-keyed lock so
 * even a degenerate React event replay can't open two tabs.
 */
export default function MarketplaceStrip({ rows, sourcePage }) {
  if (!rows || rows.length === 0) return null;

  const handleClick = (rate, event) => {
    // Iter-275.P — Single, centralized partner-open path.
    openPartner({
      partnerId: rate.slug,
      placement: sourcePage || "marketplace-strip",
      url: rate.affiliate_url,
      metadata: {
        partnerName: rate.name,
        category: rate.category,
        extraTracking: { surface: "marketplace-strip" },
      },
      event,
    });
  };

  return (
    <section
      data-testid="marketplace-strip"
      aria-label="Insurance marketplaces"
      className="mb-8"
    >
      <div className="flex items-baseline justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <Users
            className="w-4 h-4 text-[#10B981] shrink-0"
            strokeWidth={2.25}
          />
          <h3
            data-testid="marketplace-strip-heading"
            className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-950"
          >
            Compare 100+ Insurers in one place
          </h3>
        </div>
        <span className="text-[10px] uppercase tracking-[0.14em] font-bold text-slate-500">
          Marketplaces
        </span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {rows.slice(0, 3).map((r) => (
          <article
            key={r.slug}
            data-testid={`marketplace-card-${r.slug}`}
            className="border border-slate-200 bg-white hover:border-slate-950 transition-all hover:-translate-y-0.5 p-5 flex flex-col gap-4 relative overflow-hidden"
          >
            <div className="absolute top-0 left-0 w-1 h-full bg-[#10B981]" />
            {/* Iter-190.H — "Marketplace · 100+ carriers" moved to a
                dedicated top chip so it no longer floats above / through
                the logo. Layout now reads: top-row chip → logo + name +
                rating row → pitch → trust + CTA footer. */}
            <div className="flex items-center justify-between gap-3">
              <span className="inline-flex items-center text-[9px] uppercase tracking-[0.18em] font-bold text-[#10B981] bg-[#10B981]/10 border border-[#10B981]/30 px-2 py-0.5 leading-none h-5">
                Marketplace · 100+ carriers
              </span>
              {r.rating && (
                <div
                  data-testid={`marketplace-card-rating-${r.slug}`}
                  className="shrink-0 inline-flex items-center gap-1 text-xs font-bold text-slate-950"
                >
                  <Star className="w-3.5 h-3.5 fill-[#10B981] text-[#10B981]" />
                  <span className="tabular-nums">{Number(r.rating).toFixed(1)}</span>
                </div>
              )}
            </div>
            {/* Iter-190.J — Logo cell only. The duplicate text
                name beside the logo (e.g. "The Zebra The Zebra")
                read as a copy-paste bug. CarrierLogo's letterbox
                falls back to a clean "Name" wordmark when the SVG
                isn't present, so the dedicated `<p>` is removed. */}
            <div className="min-w-0">
              <CarrierLogo
                slug={r.slug}
                name={r.name}
                size="uniform"
              />
            </div>
            <p
              data-testid={`marketplace-card-pitch-${r.slug}`}
              className="text-sm text-slate-600 leading-relaxed flex-1"
            >
              {r.highlight || r.best_for ||
                "Fill one form, see side-by-side rates from 100+ carriers in minutes."}
            </p>
            <div className="flex items-center justify-between gap-3 pt-2 border-t border-slate-100">
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.14em] font-semibold text-slate-500">
                <ShieldCheck className="w-3 h-3 text-[#10B981]" />
                Free · No obligation · Soft pull
              </div>
              {r.affiliate_url ? (
                <button
                  type="button"
                  onClick={(e) => handleClick(r, e)}
                  data-testid={`marketplace-card-cta-${r.slug}`}
                  className="inline-flex items-center gap-1.5 h-9 px-3 bg-[#10B981] hover:bg-[#34D399] text-[#0A2540] font-bold text-[10px] uppercase tracking-[0.16em] transition-colors"
                >
                  Get Quote
                  <ArrowUpRight className="w-3.5 h-3.5" />
                </button>
              ) : (
                <Link
                  to="/auto-insurance#quote-form"
                  onClick={() => handleClick(r)}
                  data-testid={`marketplace-card-cta-${r.slug}`}
                  className="inline-flex items-center gap-1.5 h-9 px-3 bg-[#10B981] hover:bg-[#34D399] text-[#0A2540] font-bold text-[10px] uppercase tracking-[0.16em] transition-colors"
                >
                  Get Quote
                  <ArrowUpRight className="w-3.5 h-3.5" />
                </Link>
              )}
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}
